import { canCheckout } from "@/domain/cart/cart-validity.gates";
import { buildCartPagePricingGateContext } from "@/domain/cart/build-cart-pricing-gate";
import type { CartValidityStatus } from "@/domain/cart/cart-validity.types";
import type {
  ReorderCommitGateContext,
  ReorderPipelineFailureCode,
} from "@/domain/reorder/reorder.types";

/**
 * Whether a dry-run reorder may commit to cart.
 * Best-effort: commit when at least one line restored and checkout validity allows.
 * Validity uses {@link canCheckout} — STALE is allowed; INVALID/UNKNOWN block.
 */
export function canCommitReorder(ctx: ReorderCommitGateContext): boolean {
  if (ctx.lines.length === 0) return false;
  if (ctx.transform.errors.length > 0) return false;

  const pricing = buildCartPagePricingGateContext(ctx.lines);
  return canCheckout({ validity: ctx.validity, pricing });
}

/** Maps aggregate validity status to pipeline failure code when commit is blocked. */
export function resolveValidityFailureCode(
  status: CartValidityStatus
): ReorderPipelineFailureCode | null {
  switch (status) {
    case "VALID":
    case "STALE":
      return null;
    case "UNKNOWN":
      return "VALIDITY_UNKNOWN";
    case "INVALID":
      return "VALIDITY_INVALID";
    case "RESTAURANT_UNAVAILABLE":
      return "VALIDITY_RESTAURANT_UNAVAILABLE";
    default:
      return "VALIDITY_UNKNOWN";
  }
}
